// ONBOARDING-P3: Operator › Onboarding › a client (`/operator/onboarding/{companyId}`): the case
// in tabs. Overview (status, intake, the link, the timeline newest first), Follow-ups, Proposal
// (the editor), Seat approvals (the owner's answers on the sent version), Setup assistant (assign
// one; present the proposal or offer a call in the client's support channel), Seating (the
// runner) and Close (go-live or cancel). The operator scope routes take `company_id`; presenting
// and seating run in the client's own scope (`co`), the operator acting as its agency operator.
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Label,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ClipboardListIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import { problemText } from "../ops-parts";
import { OpsBody, OpsFrame, operatorCrumb } from "../ops-ux";
import { LoadError, Loading, Section } from "../screen-parts";
import { MessagesTab, ReviewTab } from "./onboarding-after-live";
import {
  CASE_STATUS,
  DECISION,
  day,
  FOLLOWUP_KIND,
  FOLLOWUP_STATUS,
  TIMELINE,
  when,
} from "./onboarding-labels";
import { ProposalEditor, proposalKey } from "./onboarding-proposal-editor";
import { SeatingTab } from "./onboarding-seating";

type CaseView = OperationOutput<"operator.onboarding.get">;
type ProposalView = OperationOutput<"operator.onboarding.proposal.get">;
type WorkspaceClients = OperationOutput<"operator.workspace.clients.list">;

const caseKey = (companyId: string) => ["operator", "onboarding", "case", companyId];

const TABS = [
  ["overview", "Overview"],
  ["followups", "Follow-ups"],
  ["proposal", "Proposal"],
  ["approvals", "Seat approvals"],
  ["assistant", "Setup assistant"],
  ["seating", "Seating"],
  ["close", "Close"],
  // ONBOARDING-P4: after go-live
  ["messages", "Messages"],
  ["review", "30-day review"],
] as const;
type Tab = (typeof TABS)[number][0];

export function OnboardingCasePage({ api, companyId }: { api: Api; companyId: string }) {
  const [tab, setTab] = useState<Tab>("overview");
  const q = useQuery({
    queryKey: caseKey(companyId),
    queryFn: () => api.client.operator.onboarding.get({ params: { company_id: companyId } }),
  });
  const c = q.data;
  const s = c ? (CASE_STATUS[c.status] ?? { label: c.status, variant: "neutral" as const }) : null;
  // The client's name from the workspace list when it was loaded (no second audited list read).
  const clients = useQueryClient().getQueryData<WorkspaceClients>([
    "operator",
    "workspace",
    "clients",
  ]);
  const name =
    clients?.data.find((r) => r.company.id === companyId)?.company.name ?? c?.owner.name ?? "Case";
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[
          operatorCrumb,
          { label: "Onboarding", render: <Link to="/operator/onboarding" /> },
          { label: name },
        ]}
        icon={ClipboardListIcon}
        title={c ? name : "Onboarding"}
        badge={s ? <Badge variant={s.variant}>{s.label}</Badge> : undefined}
      />
      {q.isPending ? (
        <OpsBody>
          <Loading label="Loading the onboarding case" />
        </OpsBody>
      ) : q.isError || !c ? (
        <OpsBody>
          <LoadError what="The onboarding case" error={q.error} />
        </OpsBody>
      ) : (
        <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="flex min-w-0 flex-col">
          <div className="overflow-x-auto px-6 max-md:px-4">
            <TabsList variant="line" aria-label="Onboarding case">
              {TABS.map(([v, label]) => (
                <TabsTrigger key={v} value={v}>
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <OpsBody>
            <TabsContent value="overview">
              <Overview c={c} />
            </TabsContent>
            <TabsContent value="followups">
              <Followups c={c} />
            </TabsContent>
            <TabsContent value="proposal">
              {tab === "proposal" ? (
                <WithProposal api={api} companyId={companyId}>
                  {(v) => <ProposalEditor api={api} companyId={companyId} view={v} />}
                </WithProposal>
              ) : null}
            </TabsContent>
            <TabsContent value="approvals">
              {tab === "approvals" ? (
                <WithProposal api={api} companyId={companyId}>
                  {(v) => <Approvals view={v} />}
                </WithProposal>
              ) : null}
            </TabsContent>
            <TabsContent value="assistant">
              {tab === "assistant" ? <Assistant api={api} companyId={companyId} /> : null}
            </TabsContent>
            <TabsContent value="seating">
              {tab === "seating" ? <SeatingTab api={api} co={companyId} /> : null}
            </TabsContent>
            <TabsContent value="close">
              <Close api={api} companyId={companyId} c={c} />
            </TabsContent>
            <TabsContent value="messages">
              {tab === "messages" ? <MessagesTab api={api} companyId={companyId} /> : null}
            </TabsContent>
            <TabsContent value="review">
              {tab === "review" ? <ReviewTab api={api} companyId={companyId} /> : null}
            </TabsContent>
          </OpsBody>
        </Tabs>
      )}
    </OpsFrame>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

function Overview({ c }: { c: CaseView }) {
  const timeline = [...c.timeline].sort((a, b) => b.at.localeCompare(a.at));
  return (
    <div className="flex flex-col gap-4" data-slot="onboarding-overview">
      <dl className="grid gap-3 sm:grid-cols-3">
        <Fact label="Owner">
          {c.owner.name} ({c.owner.email})
        </Fact>
        <Fact label="Plan">{c.plan_key}</Fact>
        <Fact label="Due">
          {day(c.due_at)}
          {c.overdue ? " · overdue" : ""}
        </Fact>
        <Fact label="Intake">
          {c.intake_completed_at
            ? `Complete since ${day(c.intake_completed_at)}`
            : `${c.percent}% · ${c.missing} missing`}
        </Fact>
        <Fact label="Setup assistant">{c.assistant?.name ?? "None assigned"}</Fact>
        <Fact label="Link">
          {c.link ? (
            <span>
              Not mailed: send it to the owner · <code className="break-all">{c.link}</code>
            </span>
          ) : (
            `Mailed ${day(c.link_sent_at)}`
          )}
        </Fact>
      </dl>
      {c.operator_alerted ? (
        <Notice tone="warning">
          The follow-ups ran out without a complete intake: call the owner.
        </Notice>
      ) : null}
      {c.missing_fields.length ? (
        <Section id="onboarding-missing" title="Still missing">
          <ul className="list-disc pl-5 text-sm">
            {c.missing_fields.map((m) => (
              <li key={`${m.step}.${m.field}`}>{m.label}</li>
            ))}
          </ul>
        </Section>
      ) : null}
      <Section id="onboarding-timeline" title="Timeline">
        {timeline.length ? (
          <ol className="flex flex-col gap-1.5 text-sm" data-slot="onboarding-timeline">
            {timeline.map((e, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: an append-only timeline
              <li key={i} className="flex flex-wrap gap-2">
                <span className="text-muted-foreground tabular-nums">{when(e.at)}</span>
                <span>{TIMELINE[e.kind] ?? e.kind.replaceAll("_", " ")}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">Nothing yet.</p>
        )}
      </Section>
    </div>
  );
}

function Followups({ c }: { c: CaseView }) {
  if (!c.followups.length)
    return <p className="text-sm text-muted-foreground">No follow-ups scheduled.</p>;
  return (
    <Table data-slot="onboarding-followups">
      <TableHeader>
        <TableRow>
          <TableHead scope="col">Follow-up</TableHead>
          <TableHead scope="col">Business day</TableHead>
          <TableHead scope="col">Due</TableHead>
          <TableHead scope="col">Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {c.followups.map((f) => {
          const st = FOLLOWUP_STATUS[f.status] ?? { label: f.status, variant: "neutral" as const };
          return (
            <TableRow key={`${f.kind}-${f.business_day}`}>
              <TableCell>{FOLLOWUP_KIND[f.kind] ?? f.kind}</TableCell>
              <TableCell>Day {f.business_day}</TableCell>
              <TableCell>{day(f.due_at)}</TableCell>
              <TableCell>
                <Badge variant={st.variant}>{st.label}</Badge>
                {f.done_at ? (
                  <span className="ml-2 text-xs text-muted-foreground">{day(f.done_at)}</span>
                ) : null}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function WithProposal({
  api,
  companyId,
  children,
}: {
  api: Api;
  companyId: string;
  children: (v: ProposalView) => ReactNode;
}) {
  const q = useQuery({
    queryKey: proposalKey(companyId),
    queryFn: () =>
      api.client.operator.onboarding.proposal.get({ params: { company_id: companyId } }),
  });
  if (q.isPending) return <Loading label="Loading the proposal" />;
  if (q.isError) return <LoadError what="The proposal" error={q.error} />;
  return <>{children(q.data)}</>;
}

function Approvals({ view }: { view: ProposalView }) {
  const sent = view.versions.find((v) => v.version === view.sent_version);
  if (!sent)
    return (
      <p className="text-sm text-muted-foreground">
        Nothing sent yet: the owner answers per seat once you send the proposal.
      </p>
    );
  const decided = new Map(view.decisions.map((d) => [d.seat_key, d]));
  return (
    <div className="flex flex-col gap-3" data-slot="onboarding-approvals">
      <p className="text-sm" data-slot="approved-count">
        {view.approved_seats.length} of {sent.body.seats.length} seats approved on version{" "}
        {sent.version}
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Seat</TableHead>
            <TableHead scope="col">Owner's answer</TableHead>
            <TableHead scope="col">Note</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sent.body.seats.map((s) => {
            const d = decided.get(s.key);
            const label = d ? DECISION[d.decision] : null;
            return (
              <TableRow key={s.key} data-seat={s.key}>
                <TableHead scope="row" className="font-normal">
                  {s.title}
                </TableHead>
                <TableCell>
                  {label ? (
                    <Badge variant={label.variant}>{label.label}</Badge>
                  ) : (
                    <span className="text-muted-foreground">No answer yet</span>
                  )}
                </TableCell>
                <TableCell>{d?.note ?? ""}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function Assistant({ api, companyId }: { api: Api; companyId: string }) {
  const qc = useQueryClient();
  const key = ["operator", "onboarding", "assistants", companyId];
  const q = useQuery({
    queryKey: key,
    queryFn: () =>
      api.client.operator.onboarding.assistants.list({ params: { company_id: companyId } }),
  });
  const [pick, setPick] = useState("");
  const assign = useMutation({
    mutationFn: (agent_id: string) =>
      api.client.operator.onboarding.assistant.assign({
        params: { company_id: companyId },
        body: { agent_id },
      }),
    onSuccess: (v) => {
      qc.setQueryData(key, v);
      void qc.invalidateQueries({ queryKey: caseKey(companyId) });
    },
  });
  const present = useMutation({
    mutationFn: (what: "proposal" | "call_offer") =>
      api.client.onboarding.present({ params: { co: companyId }, body: { what } }),
  });
  if (q.isPending) return <Loading label="Loading setup assistants" />;
  if (q.isError) return <LoadError what="Setup assistants" error={q.error} />;
  const v = q.data;
  const choices = v.assistants.filter((a) => a.status === "active" && a.id !== v.current?.agent_id);
  return (
    <div className="flex flex-col gap-4">
      <p data-slot="current-assistant">
        {v.current
          ? `${v.current.name} serves this client since ${day(v.current.since)}.`
          : "No setup assistant is assigned."}
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="assistant-pick">Assign another</Label>
          <NativeSelect id="assistant-pick" value={pick} onChange={(e) => setPick(e.target.value)}>
            <NativeSelectOption value="">Choose a setup assistant</NativeSelectOption>
            {choices.map((a) => (
              <NativeSelectOption key={a.id} value={a.id}>
                {a.name} ({a.clients} client{a.clients === 1 ? "" : "s"})
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
        <Button
          variant="secondary"
          disabled={!pick || assign.isPending}
          onClick={() => assign.mutate(pick)}
        >
          Assign
        </Button>
      </div>
      {assign.isError ? (
        <Notice tone="danger" role="alert">
          {problemText(assign.error, "The setup assistant could not be assigned.")}
        </Notice>
      ) : null}
      <Section
        id="onboarding-present"
        title="Present in chat"
        description="The setup assistant posts in the client's support channel."
      >
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            disabled={present.isPending}
            onClick={() => present.mutate("proposal")}
          >
            Present proposal in chat
          </Button>
          <Button
            variant="secondary"
            disabled={present.isPending}
            onClick={() => present.mutate("call_offer")}
          >
            Offer a call
          </Button>
        </div>
        {present.isError ? (
          <Notice tone="danger" role="alert">
            {problemText(present.error, "The setup assistant could not post.")}
          </Notice>
        ) : null}
        {present.isSuccess ? (
          <Notice tone="success" role="status">
            {present.data.message_id === null
              ? "Already posted in the support channel: nothing new was posted."
              : present.data.what === "proposal"
                ? `Version ${present.data.version} presented in the support channel.`
                : "Call offered in the support channel."}
          </Notice>
        ) : null}
      </Section>
    </div>
  );
}

function Close({ api, companyId, c }: { api: Api; companyId: string; c: CaseView }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<"live" | "cancelled" | null>(null);
  const close = useMutation({
    mutationFn: (outcome: "live" | "cancelled") =>
      api.client.operator.onboarding.close({
        params: { company_id: companyId },
        body: { outcome },
      }),
    onSuccess: (v) => {
      qc.setQueryData(caseKey(companyId), v);
      setConfirm(null);
    },
  });
  if (c.status === "live" || c.status === "cancelled")
    return (
      <p className="text-sm">
        Onboarding {c.status === "live" ? "went live" : "was cancelled"}
        {c.ended_at ? ` on ${day(c.ended_at)}` : ""}. The setup assistant's access has ended.
      </p>
    );
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        Closing ends onboarding: the setup assistant's access to this client is revoked.
      </p>
      {confirm ? (
        <div className="flex flex-wrap items-center gap-2" data-slot="close-confirm">
          <span className="text-sm">
            {confirm === "live" ? "Mark this client live?" : "Cancel this onboarding?"}
          </span>
          <Button
            variant={confirm === "live" ? "default" : "destructive"}
            disabled={close.isPending}
            onClick={() => close.mutate(confirm)}
          >
            {confirm === "live" ? "Confirm go-live" : "Confirm cancel"}
          </Button>
          <Button variant="ghost" onClick={() => setConfirm(null)}>
            Keep it open
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setConfirm("live")}>Go live</Button>
          <Button variant="secondary" onClick={() => setConfirm("cancelled")}>
            Cancel onboarding
          </Button>
        </div>
      )}
      {close.isError ? (
        <Notice tone="danger" role="alert">
          {problemText(close.error, "Onboarding could not be closed.")}
        </Notice>
      ) : null}
    </div>
  );
}
